import React, { useRef, useState, useEffect } from "react";
import { Modal } from "../../components/tailwind/modal/Modal";
import { Button } from "../../components/tailwind/button/Button";
import { Dropdown } from "../../components/tailwind/dropdown/Dropdown";
import {
  useUpdateCustomerFieldMutation,
  useGetCustomerFieldQuery,
  ListValue,
} from "../../api/customerFieldApi";
import { showMessage } from "../../utils/partialUtils";
import { NotificationAppearance } from "../../api/djangoToastSlice";
import { ListValuesEditorInline } from "./ListValuesEditorInline";
import { FieldType } from "../../api/enrichApi";

type Props = {
  customerFieldId: number;
  onClose: () => void;
  onSave: () => void;
};

export const CustomerFieldEditModal: React.FC<Props> = ({
  customerFieldId,
  onClose,
  onSave,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { data, isLoading: isLoadingField } = useGetCustomerFieldQuery(
    customerFieldId
  );
  const [
    updateField,
    { isLoading: isSaving },
  ] = useUpdateCustomerFieldMutation();

  const field = data?.field;
  const fieldTypeChoices = data?.field_type_choices || [];

  const [name, setName] = useState("");
  const [type, setType] = useState<string>("");
  const [entityId, setEntityId] = useState("");
  const [description, setDescription] = useState("");
  const [listValues, setListValues] = useState<ListValue[]>([]);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  useEffect(() => {
    if (field) {
      setName(field.name || "");
      setType(field.type || "");
      setEntityId(field.entity_id || "");
      setDescription(field.description || "");
      setListValues(field.list_values || []);
    }
  }, [field]);

  const handleClose = (): void => {
    dialogRef.current?.close();
    onClose();
  };

  const handleSave = async (): Promise<void> => {
    if (!field) return;

    try {
      await updateField({
        id: customerFieldId,
        name: name || undefined,
        type: type || null,
        entity_id: entityId || null,
        description: description || undefined,
        list_values: listValues.length > 0 ? listValues : null,
      }).unwrap();

      showMessage(
        NotificationAppearance.SUCCESS,
        `Updated PIM Field: ${field.field_id}`
      );
      dialogRef.current?.close();
      onSave();
    } catch (error) {
      showMessage(NotificationAppearance.ERROR, "Failed to update field");
      console.error("Failed to update field:", error);
    }
  };

  const typeOptions = fieldTypeChoices.map((choice) => ({
    key: choice.value,
    value: choice.value,
    text: choice.label,
  }));

  const isListType =
    type === FieldType.SINGLE_SELECT_LIST ||
    type === FieldType.MULTI_SELECT_LIST;
  const hasIncompleteListValues = listValues.some(
    (v) => !v.value.trim() || !v.label.trim()
  );

  if (isLoadingField || !field) {
    return (
      <Modal dialogRef={dialogRef} size="sm">
        <Modal.Header divide>
          <h5 className="tw-text-lg tw-font-semibold">Loading...</h5>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-flex tw-justify-center tw-py-8">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal dialogRef={dialogRef} size="sm">
      <Modal.Header divide>
        <h5 className="tw-text-lg tw-font-semibold">Edit PIM Field</h5>
        <p className="tw-text-sm tw-text-gray-500 tw-mt-1">
          Field ID: <span className="tw-font-mono">{field.field_id}</span>
        </p>
      </Modal.Header>
      <Modal.Body className="tw-py-6">
        <div className="tw-space-y-4">
          <div>
            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name..."
              className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2"
            />
          </div>

          <div>
            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
              Entity ID
            </label>
            <input
              type="text"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="Enter entity ID..."
              className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2"
            />
          </div>

          <div>
            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
              Type
            </label>
            <Dropdown
              options={typeOptions}
              value={type}
              onChange={(value) => setType(value as string)}
              placeholder="Select type..."
              clearable
            />
          </div>

          <div>
            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
              Description
            </label>
            <p className="tw-text-md tw-text-gray-500 tw-mb-1">
              The Description below is used as input to AI for this field when
              enriching. Adding additional instructions could improve the enrich
              result. For example if it is a measurement, be clear on the unit
              (cm/mm)
            </p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description..."
              rows={3}
              className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-blue-500 focus:tw-ring-blue-500 tw-text-sm tw-py-2 tw-px-2"
            />
          </div>

          {isListType && (
            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">
                List Values
              </label>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                <ListValuesEditorInline
                  value={listValues}
                  onChange={setListValues}
                />
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || (isListType && hasIncompleteListValues)}
          title={
            isListType && hasIncompleteListValues
              ? "Fill in all list values and labels before saving"
              : undefined
          }
        >
          Save Changes
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
